/**
 * Feature-flag framework.
 *
 * A small, reusable registry so any feature can be toggled per environment without
 * new one-off `process.env` checks scattered through the app. Flags are baked in at
 * build time from per-feature env vars (see each flag's `read` in the registry below)
 * in the target `.env.<env>` file (Docker `--build-arg APP_ENV=...`), so the same flag
 * reads differently in staging vs production from one code base.
 *
 * Adding a flag:
 *   1. Add the feature's env key (e.g. REACT_APP_FEATURE_MY_DRAFTS) to `.env.example`,
 *      `.env.staging`, and `.env.production`. scripts/check-env.mjs requires every
 *      env key the code references to be present in each deployable env, so a missing
 *      value fails the build instead of silently shipping.
 *   2. Register it below with a literal `process.env.<KEY>` read (the literal is what
 *      check-env discovers) and a `defaultEnabled` fallback.
 *   3. Export a stable key constant and read it via `useFeatureFlag(KEY)` in
 *      components or `isFeatureEnabled(KEY)` in plain code.
 *
 * `defaultEnabled` only applies when the env var is absent/blank (e.g. local dev or
 * tests). Deployed safety comes from the explicit `.env.staging` / `.env.production`
 * values plus the check-env build gate, not from the default.
 */

/** Stable flag keys. Use these constants, never the raw string, at call sites. */
export const FEATURE_MY_DRAFTS = "myDrafts";

/**
 * Flag registry. `read` returns the raw env value via a literal reference so
 * scripts/check-env.mjs can discover the key and CRA can inline it at build time.
 */
const FLAG_DEFINITIONS = {
  [FEATURE_MY_DRAFTS]: {
    read: () => process.env.REACT_APP_FEATURE_MY_DRAFTS,
    defaultEnabled: true,
    description:
      'ACR Editor "My Draft ACRs" list (the tab and index landing). Gated on in staging for client sign-off, off in production until approved (GSAACR-356).',
  },
};

/** Truthy only for an explicit "true" (case/space-insensitive); blank/undefined uses the default. */
function parseFlagValue(raw, defaultEnabled) {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return defaultEnabled;
  }
  return String(raw).trim().toLowerCase() === "true";
}

/**
 * Whether a registered feature flag is enabled in this build.
 * Unknown keys are treated as disabled (fail-closed) and warned about.
 * @param {string} flagKey one of the exported FEATURE_* constants
 * @returns {boolean}
 */
export function isFeatureEnabled(flagKey) {
  const definition = FLAG_DEFINITIONS[flagKey];
  if (!definition) {
    // eslint-disable-next-line no-console
    console.warn(`Unknown feature flag "${flagKey}"; treating as disabled.`);
    return false;
  }
  return parseFlagValue(definition.read(), definition.defaultEnabled);
}

/** Snapshot of every registered flag's current state (handy for debugging / tests). */
export function getFeatureFlags() {
  return Object.fromEntries(
    Object.keys(FLAG_DEFINITIONS).map((key) => [key, isFeatureEnabled(key)])
  );
}
